import Layout from '../../components/Layout';
import { Link, useLocation } from 'react-router-dom';
import Stack from '@mui/material/Stack';
import { DynamicIcon, type IconName } from 'lucide-react/dynamic';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import Breadcrumb from '@/components/Breadcrumb';

// Lazy loading for performance optimization
import { lazy, Suspense } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import MenuBar from '@/components/MenuBar';
import Card from '@/components/Card';
import { useTranslation } from 'react-i18next';

// Dynamic imports for code splitting
const Variables = lazy(() => import('./Variables'));
const StandardValues = lazy(() => import('./StandardValues'));
const Users = lazy(() => import('./Users'));
const Files = lazy(() => import('./Files'));
const Parameters = lazy(() => import('./Mapping'));
const Models = lazy(() => import('./Models'));
const Homologation = lazy(() => import('./Homologation'));
const Reports = lazy(() => import('./Reports'));

/**
 * Interface for navigation items in the settings menu
 */
interface LinkItem {
  path: string;
  icon: IconName;
  label: string;
}

/**
 * Main system settings page
 * Manages navigation between different settings sections
 * with responsive layout and optimized component loading
 */
function Settings() {
  const location = useLocation();
  const { t } = useTranslation();
  // Extract current route by removing '/settings/' prefix
  const currentPath = location.pathname.replace('/settings/', '');
  const theme = useTheme();

  // TODO: Remove debug console.log in production
  console.log(currentPath);

  // Breakpoint for responsive layout
  //const isLg = useMediaQuery('(max-width:700px)');
  //const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  //const isMd = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isLg = useMediaQuery(theme.breakpoints.between('md', 'lg'));

  /**
   * List of navigation links for settings sections
   * Each item represents an available settings page
   */
  const links: LinkItem[] = [
    { path: '/variables', icon: 'book-user', label: t('settings.variables') },
    {
      path: '/standardValues',
      icon: 'boxes',
      label: t('settings.standardValues'),
    },
    {
      path: '/parameters',
      icon: 'settings-2',
      label: t('settings.parameters'),
    },
    // { path: '/automations', icon: 'bot', label: t('settings.automations') },
    { path: '/users', icon: 'users', label: t('settings.users') },
    { path: '/files', icon: 'file-input', label: t('settings.files') },
    { path: '/models', icon: 'router', label: t('settings.models') },
    {
      path: '/homologation',
      icon: 'shield-check',
      label: t('settings.homologation'),
    },
    { path: '/reports', icon: 'clipboard-list', label: t('settings.reports') },
  ];

  return (
    <Layout noLeftMargin>
      {/* 
        Render grid of cards when on main settings route
        Responsive layout: grid on desktop, column on mobile
      */}
      {currentPath === '/settings' && (
        <Stack
          width="100%"
          height="100%"
          justifyContent="center"
          display="flex"
          alignItems="center"
        >
          <Stack
            direction={isLg ? 'column' : 'row'}
            mt="24px"
            display={isLg ? 'flex' : 'grid'}
            gridTemplateColumns="repeat(3, 1fr)"
            gap="16px"
            width={isLg ? '50%' : '550px'}
            height={isLg ? '100%' : '40%'}
          >
            {links.map((link) => (
              <Card
                key={link.path}
                sx={{
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '8px',
                  height: '100%',
                }}
              >
                <Link
                  to={'/settings' + link.path}
                  style={{
                    textDecoration: 'none',
                    color: 'currentcolor',
                  }}
                >
                  {/* Responsive icon: smaller size on mobile */}
                  <DynamicIcon name={link.icon} size={isLg ? '14px' : '24px'} />
                  <Typography
                    variant={isLg ? 'h6' : 'h5'}
                    fontWeight={500}
                    color="currentColor"
                  >
                    {link.label}
                  </Typography>
                </Link>
              </Card>
            ))}
          </Stack>
        </Stack>
      )}

      {/* 
        Render specific section content when not on main settings route
        Includes breadcrumb, navigation menu, and lazy-loaded content
      */}
      {currentPath !== '/settings' && (
        <Stack
          spacing={1}
          width="90%"
          padding="8px"
          alignItems="flex-start"
          overflow="hidden"
        >
          {/* Navigation breadcrumb */}
          <Breadcrumb
            paths={[
              { label: t('breadcrumbs.dashboard'), route: '/' },
              { label: t('breadcrumbs.settings'), route: '/settings' },
              {
                label: t(`breadcrumbs.${currentPath}`),
                route: `/settings/${currentPath}`,
              },
            ]}
          />

          {/* Side menu bar with navigation links */}

          <MenuBar links={links} currentPath={currentPath} />

          {/* 
            Suspense boundary for lazy loading components
            Shows loading indicator while component is being loaded
          */}
          <Suspense
            fallback={
              <Box
                sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}
              >
                <CircularProgress />
              </Box>
            }
          >
            {/* Conditional rendering of components based on current route */}
            {currentPath === 'variables' && <Variables />}
            {currentPath === 'standardValues' && <StandardValues />}
            {currentPath === 'users' && <Users />}
            {currentPath === 'files' && <Files />}
            {currentPath === 'parameters' && <Parameters />}
            {currentPath === 'models' && <Models />}
            {currentPath === 'homologation' && <Homologation />}
            {currentPath === 'reports' && <Reports />}
          </Suspense>
        </Stack>
      )}
    </Layout>
  );
}

export default Settings;
